import { CognitoService } from '@/auth/CognitoService';
import type { AppPlan } from './plan/planTypes';

/**
 * Planning and building, asked of the server.
 *
 * The pipeline itself — the prompt, the gate, the repair loop, the design kits, the
 * runtime inlined into every published page — used to live here, in the binary. On the web
 * that only meant a redeploy. On iOS it meant that changing a sentence in a prompt needed
 * a new build and an App Store review, which put the fastest-moving part of the product
 * behind the slowest door in it. It runs on the server now, and this asks it to.
 *
 * A build takes the better part of a minute, so it goes to a Lambda Function URL rather
 * than the API, which stops at twenty-nine seconds.
 */
const BUILD_URL = (process.env.EXPO_PUBLIC_BUILD_URL ?? '').replace(/\/+$/, '');

export class BuildServiceError extends Error {
  constructor(message: string, readonly reason?: string) {
    super(message);
    this.name = 'BuildServiceError';
  }
}

/** True when the app is configured to build on the server. */
export function serverBuildConfigured(): boolean {
  return !!BUILD_URL;
}

async function call<T>(path: string, body: unknown): Promise<T> {
  if (!BUILD_URL) throw new BuildServiceError('This build needs a connection and has none.');
  const token = CognitoService.getAccessTokenSync();

  const res = await fetch(`${BUILD_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(text) as Record<string, unknown>; } catch { /* keep empty */ }

  if (!res.ok) {
    // The server's own reason, so the UI can route a credit or sign-in denial the same way
    // it always has rather than showing "something went wrong" for both.
    const reason = typeof data.error === 'string' ? data.error : undefined;
    throw new BuildServiceError(messageFor(reason, res.status), reason);
  }
  return data as T;
}

function messageFor(reason: string | undefined, status: number): string {
  if (reason === 'signup_required') return 'Sign in to build an app.';
  if (reason === 'insufficient_credits') return 'You are out of credits.';
  if (status >= 500) return 'The build service had a problem. Try again.';
  return 'That build could not be started.';
}

export async function planOnServer(goal: string): Promise<{ plan: AppPlan; refined: boolean }> {
  return call<{ plan: AppPlan; refined: boolean }>('/plan', { goal });
}

export interface ServerBuild {
  appId: string;
  appName: string;
  /** The finished document, runtime already inlined. */
  html: string;
  appCode: string;
  attempts: number;
  repairRounds: number;
  gateFailures: number;
}

export async function buildOnServer(input: {
  goal: string;
  appName: string;
  vibe?: string | null;
  plan?: AppPlan | null;
}): Promise<ServerBuild> {
  return call<ServerBuild>('/build', input);
}

/**
 * The app's own code, wrapped in the current shell — by the server.
 *
 * Assembling in the client meant a runtime fix reached an existing app only when somebody
 * republished it from a phone running a new enough binary. The document is the same; where
 * it is built decides who can receive a fix.
 */
export async function assembleOnServer(input: {
  appId: string;
  appName: string;
  source: string;
  accent?: string;
  description?: string;
}): Promise<{ html: string }> {
  return call<{ html: string }>('/assemble', input);
}
